import { createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = {
  class: "px-4 pb-4"
};
const _hoisted_2 = {
  key: 1
};
const _hoisted_3 = {
  class: "w-1/2"
};
const _hoisted_4 = {
  class: "flex py-2 mb-20"
};
const _hoisted_5 = {
  class: "w-2/3"
};
const _hoisted_6 = {
  class: "w-1/2"
};
const _hoisted_7 = {
  class: "space-y-2 py-2"
};
import { reactive, computed, onMounted } from 'vue';
import RouteService from '@/services/RouteService';
import useClientMatterStore from '@/services/useClientMatterStore';
import { useSnackbarHandler } from '@/composables/useSnackbarHandler';
import { useMeStore } from '@/services/UserService/useMeStore';
import { storeToRefs } from 'pinia';

// Component Imports
import Loader from '@/components/Loader.vue';
import LlCheckbox from '@/components/LlCheckbox.vue';
import LlTextField from '@/components/LlTextField.vue';
import LlSelect from '@/components/LlSelect.vue';
import SettingsSaveBar from '@/components/SettingsSaveBar.vue';
import LlTextarea from '@/components/LlTextarea.vue';
import SnackbarComponent from '@/components/SnackbarComponent.vue';
export default {
  __name: 'MatterLedes',
  props: {
    stateParams: Object,
    state: Object,
    rootScope: Object
  },
  setup(__props) {
    const props = __props;
    const clientMatterStore = useClientMatterStore();
    const routeService = new RouteService(props);
    const {
      handleError,
      handleSuccess
    } = useSnackbarHandler();
    const meStore = useMeStore();
    const {
      features
    } = storeToRefs(meStore);
    const matterLedesState = reactive({
      loading: true,
      defaultLedesId: '',
      unsaved: false,
      saving: false,
      loadedCodes: {},
      hydrated: false,
      form: {
        externalId: '',
        ledesId: '',
        matterDescription: '',
        ledesInvoiceDescription: '',
        insertResponsibleAttorneyAsManagingContact: false,
        includeZeroDollarItems: null,
        ledesSplitAdjustment: null,
        ledesSplitAdjustmentDescription: ''
      },
      originalForm: {
        externalId: '',
        ledesId: '',
        matterDescription: '',
        ledesInvoiceDescription: '',
        insertResponsibleAttorneyAsManagingContact: false,
        includeZeroDollarItems: null,
        ledesSplitAdjustment: null,
        ledesSplitAdjustmentDescription: ''
      },
      includeZeroDollarItemsOptions: [{
        label: 'Use Client Default',
        value: null
      }, {
        label: 'Include $0 Items',
        value: true
      }, {
        label: 'Exclude $0 Items',
        value: false
      }],
      splitBillingMethodOptions: [{
        label: 'Full Invoice (No Adjustment)',
        value: 'fullInvoice'
      }, {
        label: 'Invoice-Level Adjustment',
        value: 'invoiceLevelAdjustment'
      }]
    });
    onMounted(async () => {
      const matter = await loadMatter();
      if (matter) {
        matterLedesState.defaultLedesId = truncateId(matter.reference || matter.name, 25);
      }
      setupRouteChangeHandler();
    });
    const somethingChanged = computed(() => JSON.stringify(matterLedesState.form) !== JSON.stringify(matterLedesState.originalForm));
    function truncateId(id, length) {
      return id?.length > length ? id.substring(0, length) : id;
    }
    function isActiveMatterRoute(expectedMatterId) {
      return String(props.stateParams?.matterId) === String(expectedMatterId);
    }
    function loadMatter() {
      const requestedMatterId = props.stateParams?.matterId;
      if (!requestedMatterId) {
        return Promise.resolve(null);
      }
      matterLedesState.loading = true;
      // Force a fresh fetch every visit - LEDES settings can change from another
      // tab/user/bulk action, and the store's per-id memoization would otherwise
      // silently serve a stale cached matter for the rest of the session.
      return clientMatterStore.getMatterById(requestedMatterId, true).then(matter => {
        if (!isActiveMatterRoute(requestedMatterId)) {
          return null;
        }
        if (matter) {
          matterLedesState.loadedCodes = matter.settings?.codes || {};
          matterLedesState.originalForm = hydrateForm(matter);
          resetForm();
          matterLedesState.hydrated = true;
        }
        return matter;
      }).catch(err => {
        handleError(err);
        return null;
      }).finally(() => {
        matterLedesState.loading = false;
      });
    }
    function setupRouteChangeHandler() {
      routeService.rootScopeChange().$on('$stateChangeStart', event => {
        if (somethingChanged.value) {
          matterLedesState.unsaved = true;
          setTimeout(() => {
            matterLedesState.unsaved = false;
          }, 4000);
          event.preventDefault();
        }
      });
    }
    function resetForm() {
      matterLedesState.form = JSON.parse(JSON.stringify(matterLedesState.originalForm));
    }
    function hydrateForm(source = {}) {
      // Find the matching option object for the dropdown
      const includeZeroDollarItemsValue = source.settings?.codes?.includeZeroDollarItems;
      const includeZeroDollarItemsOption = matterLedesState.includeZeroDollarItemsOptions.find(option => option.value === includeZeroDollarItemsValue) || matterLedesState.includeZeroDollarItemsOptions[0]; // Default to "Use Client Default"

      // Find the matching option for split billing
      const ledesSplitAdjustment = source.settings?.codes?.ledesSplitAdjustment;
      const splitBillingOption = ledesSplitAdjustment ? matterLedesState.splitBillingMethodOptions.find(option => option.value === ledesSplitAdjustment.method) || null // If no match found, set to null (cleared)
      : null; // Default to null (no selection)

      return {
        externalId: source.settings?.codes?.externalId || '',
        ledesId: source.ledesId || '',
        matterDescription: source.settings?.codes?.matterDescription || '',
        ledesInvoiceDescription: source.ledesInvoiceDescription || '',
        insertResponsibleAttorneyAsManagingContact: source.settings?.codes?.insertResponsibleAttorneyAsManagingContact || false,
        includeZeroDollarItems: includeZeroDollarItemsOption,
        ledesSplitAdjustment: splitBillingOption,
        ledesSplitAdjustmentDescription: ledesSplitAdjustment?.description || ''
      };
    }
    async function update() {
      const requestedMatterId = props.stateParams?.matterId;
      if (!requestedMatterId) {
        return;
      }
      if (!matterLedesState.hydrated) {
        return;
      }
      matterLedesState.saving = true;
      matterLedesState.unsaved = false;

      // Extract the value from the dropdown option objects
      const formData = {
        ...matterLedesState.form,
        includeZeroDollarItems: matterLedesState.form.includeZeroDollarItems?.value ?? null,
        ledesSplitAdjustment: matterLedesState.form.ledesSplitAdjustment?.value ? {
          method: matterLedesState.form.ledesSplitAdjustment.value,
          description: matterLedesState.form.ledesSplitAdjustmentDescription || ''
        } : null
      };
      const codes = {
        ...matterLedesState.loadedCodes,
        ...formData
      };
      try {
        const matter = await clientMatterStore.updateMatter({
          id: requestedMatterId,
          update: {
            ...formData,
            settings: {
              codes
            }
          }
        });

        // updateMatter returns null when user navigates away before response arrives.
        if (!matter || !isActiveMatterRoute(requestedMatterId)) {
          return;
        }
        matterLedesState.loadedCodes = matter.settings?.codes || {};
        matterLedesState.originalForm = hydrateForm(matter);
        resetForm();
        handleSuccess('Settings updated successfully');
      } catch (err) {
        handleError(err);
      } finally {
        matterLedesState.saving = false;
      }
    }
    function goToClientSettings() {
      routeService.go('clients.client.view.ledes', {
        id: props.stateParams.id
      });
    }
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createVNode(SnackbarComponent, {
        ref: "snackbar"
      }, null, 512), matterLedesState.loading ? (_openBlock(), _createBlock(Loader, {
        key: 0,
        what: "LEDES settings",
        class: "mt-32"
      })) : (_openBlock(), _createElementBlock("div", _hoisted_2, [_createElementVNode("div", _hoisted_3, [_createElementVNode("p", null, [_cache[8] || (_cache[8] = _createTextVNode(" These are your LEDES settings for this matter. To make changes at the client level, ", -1)), _createElementVNode("span", {
        onClick: _withModifiers(goToClientSettings, ["prevent"]),
        class: "cursor-pointer text-primary font-medium"
      }, "click here.")])]), _createElementVNode("div", _hoisted_4, [_cache[10] || (_cache[10] = _createElementVNode("div", {
        class: "w-1/3"
      }, [_createElementVNode("div", {
        class: "text-lg py-2"
      }, "LEDES Billing")], -1)), _createElementVNode("div", _hoisted_5, [_createElementVNode("div", _hoisted_6, [_cache[9] || (_cache[9] = _createElementVNode("div", {
        class: "text-base py-2"
      }, "Matter Settings", -1)), _createElementVNode("div", _hoisted_7, [_createVNode(LlTextField, {
        label: "LEDES Client Matter Id",
        info: "This id will be assigned to the CLIENT_MATTER_ID field on the LEDES invoice and is the id for the matter as provided by the recipient of the invoice",
        modelValue: matterLedesState.form.externalId,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = $event => matterLedesState.form.externalId = $event)
      }, null, 8, ["modelValue"]), _createVNode(LlTextField, {
        label: "LEDES Law Firm Matter Id",
        info: "This determines the LAW_FIRM_MATTER_ID field on the LEDES invoice. By default, the matter reference from LeanLaw goes here, otherwise the matter name. You can set it to a specific value here.",
        placeholder: matterLedesState.defaultLedesId,
        modelValue: matterLedesState.form.ledesId,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = $event => matterLedesState.form.ledesId = $event)
      }, null, 8, ["placeholder", "modelValue"]), _createVNode(LlTextarea, {
        label: "Matter Description",
        modelValue: matterLedesState.form.matterDescription,
        "onUpdate:modelValue": _cache[2] || (_cache[2] = $event => matterLedesState.form.matterDescription = $event)
      }, null, 8, ["modelValue"]), _createVNode(LlTextField, {
        label: "Default Invoice Description",
        placeholder: "Draft/Interim/Final",
        info: "This overrides the description normally assigned to invoices (draft = \"Draft\", approved = \"Interim\", open = \"Final\").",
        modelValue: matterLedesState.form.ledesInvoiceDescription,
        "onUpdate:modelValue": _cache[3] || (_cache[3] = $event => matterLedesState.form.ledesInvoiceDescription = $event)
      }, null, 8, ["modelValue"])]), _createVNode(LlCheckbox, {
        label: "Insert Responsible Attorney as Managing Contact",
        modelValue: matterLedesState.form.insertResponsibleAttorneyAsManagingContact,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = $event => matterLedesState.form.insertResponsibleAttorneyAsManagingContact = $event)
      }, null, 8, ["modelValue"]), _createVNode(LlSelect, {
        modelValue: matterLedesState.form.includeZeroDollarItems,
        "onUpdate:modelValue": _cache[5] || (_cache[5] = $event => matterLedesState.form.includeZeroDollarItems = $event),
        items: matterLedesState.includeZeroDollarItemsOptions,
        "item-title": "label",
        "item-value": "value",
        "return-object": "",
        placeholder: "Use Client Default",
        variant: "outlined",
        label: "Include $0 Items in LEDES",
        labelClass: "text-sm at-choose-client remove-outline",
        info: "Control whether $0 time entries, expenses, and fixed fees are included in LEDES exports for this matter. When set to 'Use Client Default', the client's setting will be applied.",
        "data-cy": "include-zero-dollar-items-dropdown",
        isAutocomplete: ""
      }, null, 8, ["modelValue", "items"]), _unref(features)?.leedesSplitFf === true ? (_openBlock(), _createBlock(LlSelect, {
        key: 0,
        modelValue: matterLedesState.form.ledesSplitAdjustment,
        "onUpdate:modelValue": _cache[6] || (_cache[6] = $event => matterLedesState.form.ledesSplitAdjustment = $event),
        items: matterLedesState.splitBillingMethodOptions,
        "item-title": "label",
        "item-value": "value",
        "return-object": "",
        placeholder: "Select method",
        variant: "outlined",
        label: "Split Billing Method",
        labelClass: "text-sm at-choose-client remove-outline",
        info: "Configure how LEDES invoices should be adjusted when multiple payors share costs for this matter.",
        "data-cy": "split-billing-method-dropdown",
        isAutocomplete: "",
        clearable: ""
      }, null, 8, ["modelValue", "items"])) : _createCommentVNode("", true), _unref(features)?.leedesSplitFf === true && matterLedesState.form.ledesSplitAdjustment && matterLedesState.form.ledesSplitAdjustment?.value !== 'fullInvoice' ? (_openBlock(), _createBlock(LlTextField, {
        key: 1,
        modelValue: matterLedesState.form.ledesSplitAdjustmentDescription,
        "onUpdate:modelValue": _cache[7] || (_cache[7] = $event => matterLedesState.form.ledesSplitAdjustmentDescription = $event),
        label: "Adjustment Description (Optional)",
        placeholder: "Description for this split billing configuration",
        maxlength: 200,
        info: "Optional description to help identify this split billing configuration. Maximum 200 characters.",
        "data-cy": "split-billing-description-field"
      }, null, 8, ["modelValue"])) : _createCommentVNode("", true)])])]), _createVNode(SettingsSaveBar, {
        somethingChanged: somethingChanged.value,
        saving: matterLedesState.saving,
        unsaved: matterLedesState.unsaved,
        state: __props.state,
        stateParams: __props.stateParams,
        showFullWidth: "",
        onCancel: resetForm,
        onUpdate: update
      }, null, 8, ["somethingChanged", "saving", "unsaved", "state", "stateParams"])]))]);
    };
  }
};